import { useTranslation } from 'react-i18next';

import { MarkTile } from '@/shared/ui';

import { useOmniboxStore } from '../model/omniboxStore';
import { PREFIXES } from '../model/types';
import { OmniboxSky } from './OmniboxSky';
import styles from './OmniboxTrigger.module.css';

/**
 * Wejście do omniboksa — **na pełną szerokość paska, minus kółko użytkownika**.
 *
 * Wygląda jak pole, jest PRZYCISKIEM: prawdziwy `input` w powłoce dublowałby ognisko
 * z polem w nakładce i mylił czytnik ekranu (dwa pola szukania, z których jedno nic
 * nie robi).
 *
 * ── Dlaczego pełna szerokość i dlaczego pigułki ───────────────────────────────
 * Decyzja 55 odrzuciła mega-wyszukiwarkę, ale odrzuciła **wysokość trzech wierszy**,
 * nie szerokość — charakter ma nieść materiał, nie rozmiar. Szerokość była więc wolna,
 * ale musi na siebie zarobić: tysiąc pikseli pustego pola z małym „Szukaj" wygląda
 * gorzej niż wąskie pole. ⭐ Dlatego niesie **pigułki prefiksów** — pole mówi, co umie,
 * a to jest jedyna wiedza o składni, której człowiek nie ma dziś skąd wziąć (tooltipy
 * są wycofane decyzją 27, a Słownik jeszcze nie powstał).
 *
 * ⚠ Kolejność pigułek jest **wpisana tutaj, nie wzięta z `PREFIXES`**: tamta tablica
 * jest posortowana pod parser (tryby szukania najpierw), a tu chodzi o naukę składni —
 * najpierw to, co się tworzy. `PREFIXES` zostaje jednak **źródłem samych znaków**, żeby
 * pigułka nie mogła obiecać prefiksu, którego parser nie zna.
 */

const PILL_ORDER = ['createElement', 'createRelation', 'createCommitment', 'searchFullText'] as const;

export function OmniboxTrigger() {
  const { t } = useTranslation();
  const open = useOmniboxStore((state) => state.open);

  return (
    <button type="button" className={styles['trigger']} onClick={() => open()}>
      <MarkTile size={28} />

      <span className={styles['placeholder']}>{t('search.open')}</span>

      {/* Konstelacja stoi PRZED pigułkami i po napisie — to jest wariant P1
          („trójka w luce"), więc ozdoba nigdy nie trafia pod tekst. */}
      <OmniboxSky className={styles['sky']} />

      <span className={styles['pills']}>
        {PILL_ORDER.map((kind) => {
          const prefix = PREFIXES.find((entry) => entry.kind === kind)?.prefix;

          return prefix === undefined ? null : (
            <span key={kind} className={styles['pill']}>
              <b>{prefix}</b> {t(`search.pill.${kind}`)}
            </span>
          );
        })}
      </span>

      <kbd className={styles['kbd']}>{t('search.shortcut')}</kbd>
    </button>
  );
}
